import { inject, Injectable } from '@angular/core';
import { assertGraphqlDataNode } from '@reconcept-platform/shared/data-access';
import {
  type CreateDashboardRoleInput,
  type CreateDashboardRoleMutationMutation,
  type CreateDashboardRoleMutationMutationVariables,
  type CreateUserWithDashboardRoleAndWorkspaceRolesMutationMutation,
  type CreateUserWithDashboardRoleAndWorkspaceRolesMutationMutationVariables,
  type DashboardRoleFragmentFragment,
  type DashboardRoleQueryQuery,
  type DashboardRoleQueryQueryVariables,
  type DestroyDashboardRoleInput,
  type DestroyDashboardRoleMutationMutation,
  type DestroyDashboardRoleMutationMutationVariables,
  type FindOrCreateUserWithDashboardRoleAndWorkspaceRolesInput,
  type UpdateDashboardRoleInput,
  type UpdateDashboardRoleMutationMutation,
  type UpdateDashboardRoleMutationMutationVariables,
} from '@reconcept-platform/shared/types';
import { gql } from 'apollo-angular';
import { type Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { invalidateAllWorkspaces } from '../../utils/cache-invalidators.utils';
import { WorkspaceGraphQLService } from '../../workspace-graphql.service';
import { dashboardWithRolesQuery } from '../dashboard/dashboard.queries';
import { workspaceWithRolesQuery } from '../workspace/workspace.queries';
import { DashboardRoleQuery } from './dashbpard-role.queries';

@Injectable({
  providedIn: 'root',
})
export class DashboardRoleApi {
  private workspaceGraphQLService = inject(WorkspaceGraphQLService);

  watchDashboardRole(roleId: string): Observable<DashboardRoleFragmentFragment> {
    return this.workspaceGraphQLService
      .watchQuery<DashboardRoleQueryQuery, DashboardRoleQueryQueryVariables>({
        query: DashboardRoleQuery,
        variables: {
          id: roleId,
        },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'DashboardRole')));
  }

  createDashboardRole(createDashboardRoleInput: CreateDashboardRoleInput) {
    return (
      this.workspaceGraphQLService
        .mutateV2<CreateDashboardRoleMutationMutation, CreateDashboardRoleMutationMutationVariables>({
          mutation: gql`
            mutation CreateDashboardRoleMutation($input: CreateDashboardRoleInput!) {
              createDashboardRole(input: $input) {
                dashboardRole {
                  id
                  title
                  accessLevel
                  user {
                    id
                  }
                  dashboard {
                    id
                  }
                }
                errors {
                  path
                  message
                }
              }
            }
          `,
          variables: {
            input: createDashboardRoleInput,
          },
          refetchQueries: [
            {
              query: dashboardWithRolesQuery,
              variables: {
                id: createDashboardRoleInput.dashboardId,
              },
            },
          ],
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.createDashboardRole.dashboardRole))
    );
  }

  createUserWithDashboardRoleAndWorkspaceRoles(input: FindOrCreateUserWithDashboardRoleAndWorkspaceRolesInput) {
    const workspaceIds = (input.workspaceRolesAttributes ?? []).map((workspaceRole) => workspaceRole.workspaceId);

    return (
      this.workspaceGraphQLService
        .mutateV2<
          CreateUserWithDashboardRoleAndWorkspaceRolesMutationMutation,
          CreateUserWithDashboardRoleAndWorkspaceRolesMutationMutationVariables
        >({
          mutation: gql`
            mutation CreateUserWithDashboardRoleAndWorkspaceRolesMutation(
              $input: FindOrCreateUserWithDashboardRoleAndWorkspaceRolesInput!
            ) {
              findOrCreateUserWithDashboardRoleAndWorkspaceRoles(input: $input) {
                user {
                  id
                }
                errors {
                  path
                  message
                }
              }
            }
          `,
          variables: {
            input,
          },
          refetchQueries: [
            ...(input.dashboardRoleAttributes?.dashboardId
              ? [
                  {
                    query: dashboardWithRolesQuery,
                    variables: {
                      id: input.dashboardRoleAttributes.dashboardId,
                    },
                  },
                ]
              : []),
            ...workspaceIds.map((id) => ({
              query: workspaceWithRolesQuery,
              variables: { id },
            })),
          ],
        })
        // @ts-expect-error TS18048 possibly undefined
        .pipe(map((result) => result.data.findOrCreateUserWithDashboardRoleAndWorkspaceRoles))
    );
  }

  updateDashboardRole(updateDashboardRoleInput: UpdateDashboardRoleInput) {
    return (
      this.workspaceGraphQLService
        .mutateV2<UpdateDashboardRoleMutationMutation, UpdateDashboardRoleMutationMutationVariables>({
          mutation: gql`
            mutation UpdateDashboardRoleMutation($input: UpdateDashboardRoleInput!) {
              updateDashboardRole(input: $input) {
                dashboardRole {
                  id
                  title
                  accessLevel
                  user {
                    id
                  }
                  dashboard {
                    id
                  }
                }
                errors {
                  path
                  message
                }
              }
            }
          `,
          variables: {
            input: updateDashboardRoleInput,
          },
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.updateDashboardRole.dashboardRole))
    );
  }

  destroyDashboardRole(destroyDashboardRoleInput: DestroyDashboardRoleInput, dashboardId: string) {
    const cascade = destroyDashboardRoleInput.alsoWorkspaceRolesWhereViewerCanManage;

    return (
      this.workspaceGraphQLService
        .mutateV2<DestroyDashboardRoleMutationMutation, DestroyDashboardRoleMutationMutationVariables>({
          mutation: gql`
            mutation DestroyDashboardRoleMutation($input: DestroyDashboardRoleInput!) {
              destroyDashboardRole(input: $input) {
                dashboardRole {
                  id
                }
                errors {
                  path
                  message
                }
              }
            }
          `,
          variables: {
            input: destroyDashboardRoleInput,
          },
          refetchQueries: [
            {
              query: dashboardWithRolesQuery,
              variables: {
                id: dashboardId,
              },
            },
          ],
          // the cascade also destroys the user's roles in the dashboard's workspaces; the affected workspace ids
          // are unknown here, so evict every cached workspace role list
          update: cascade ? invalidateAllWorkspaces(['workspaceRoles']) : undefined,
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.destroyDashboardRole.dashboardRole))
    );
  }
}
